<script>
import { GraphQLClient } from "graphql-request";

export default {
  props: {
    gql: String,
    variables: Object,
    hasInput: {
      type: Boolean,
      default: true,
    },
    maxRetries: {
      type: Number,
      default: 6, // Max retry attempts
    },
    baseDelay: {
      type: Number,
      default: 1000, // Base delay (in ms)
    },
  },
  data() {
    return {
      data: null,
      error: null,
      loading: true,
      isRetrying: false, // Indicates if retries are ongoing
    };
  },
  computed: {
    gqlConfig() {
      return this.$store?.state?.config;
    },
    gqlOptions() {
      if (!this.gqlConfig?.token) return;
      return {
        headers: {
          authorization: `Bearer ${this.gqlConfig.token}`,
        },
      };
    },
    gqlClient() {
      if (!this.gqlConfig?.endpoint) return;
      return new GraphQLClient(this.gqlConfig.endpoint, this.gqlOptions);
    },
    gqlParams() {
      if (!this.gqlConfig?.companyId) return;
      if (!this.hasInput) {
        return {
          company_id: this.gqlConfig.companyId,
          ...this?.variables,
        };
      }
      return {
        input: {
          company_id: this.gqlConfig.companyId,
          ...this?.variables?.input,
        },
      };
    },
  },
  methods: {
    async gqlRequest(retryCount = 0) {
      try {
        this.isRetrying = retryCount > 0;
        this.loading = true;

        this.data = await this.gqlClient.request(this.gql, this.gqlParams);
        this.$emit("result", this.data);
        this.error = null;
      } catch (error) {
        console.error(`Attempt ${retryCount + 1} failed`, error);

        let delay = Math.min(10000 * Math.pow(2, retryCount), 41000);

        console.log(`Retrying in ${delay / 1000} seconds (Attempt ${retryCount + 1})`);

        if (retryCount >= this.maxRetries) {
          this.error = JSON.stringify(error, undefined, 2);
          this.$emit("error", this.error);
          return;
        }

        await this.sleep(delay);

        await this.gqlRequest(retryCount + 1);
      } finally {
        this.isRetrying = false;
        this.loading = false;
      }
    },

    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
  },

  watch: {
    variables: {
      handler(value, oldValue) {
        if (!value) return;
        if (JSON.stringify(value) !== JSON.stringify(oldValue)) {
          this.loading = true;
          this.gqlRequest();
        }
      },
      deep: true,
    },
    loading: {
      handler(val) {
        this.$emit("loading", val);
      },
    },
  },
  mounted() {
    this.gqlRequest();
  },
  render() {
    return this.$scopedSlots.default({
      data: this.data,
      error: this.isRetrying ? null : this.error, // Hide error during retries
      loading: this.loading,
      gqlRequest: this.gqlRequest,
    });
  },
};
</script>
